import type {
  NotificationRecord,
  NotificationType,
} from '../models/notification.model'
import { notificationsRepo } from './notifications.repo'

const generateId = () =>
  `n_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`

export interface NotifyInput {
  type: NotificationType
  title: string
  message: string
  actionUrl?: string
  metadata?: NotificationRecord['metadata']
}

export const notificationsService = {
  list(): NotificationRecord[] {
    return notificationsRepo.all()
  },

  notify(input: NotifyInput): NotificationRecord {
    return notificationsRepo.insert({
      id: generateId(),
      type: input.type,
      title: input.title,
      message: input.message,
      actionUrl: input.actionUrl,
      metadata: input.metadata,
      timestampMs: Date.now(),
      isRead: false,
    })
  },

  /**
   * Idempotent: only inserts the notification if no existing record carries
   * the same `metadata.reminderKey`. Returns the new record or `null` if it
   * was a duplicate.
   */
  notifyReminderOnce(
    reminderKey: string,
    input: NotifyInput,
  ): NotificationRecord | null {
    if (notificationsRepo.hasReminderEmitted(reminderKey)) return null
    return this.notify({
      ...input,
      metadata: { ...input.metadata, reminderKey },
    })
  },

  markAsRead(id: string) {
    notificationsRepo.update(id, { isRead: true })
  },

  markAllAsRead() {
    const all = notificationsRepo.all()
    notificationsRepo.bulkReplace(all.map((n) => ({ ...n, isRead: true })))
  },

  remove(id: string) {
    notificationsRepo.rememberClearedReminders(notificationsRepo.all().filter((n) => n.id === id))
    notificationsRepo.remove(id)
  },

  clearAll() {
    notificationsRepo.rememberClearedReminders(notificationsRepo.all())
    notificationsRepo.bulkReplace([])
  },
}
